import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { SettingsService } from './settings.service';
import { Profile } from '../models/search.model';
import { WebApiModel } from '../models/web-api.model';

@Injectable()
export class SearchService {
  constructor(private http: HttpClient) {}

  getPlaces(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    pageNumber: number,
    pageSize: number,
    serviceCategory: string,
    service: string,
    specialty: string,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): Observable<Profile[]> {
    const params = this.buildSearchParams(
      name,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      pageNumber,
      pageSize,
      serviceCategory,
      service,
      specialty,
      latitude,
      longitude,
      distance,
      distanceKms,
    );

    let serviceUrl = SettingsService.api.searchPlacesByNameProfileUrl;

    if (location) {
      serviceUrl = SettingsService.api.searchPlacesByNameAndLocationProfileUrl;
    } else if (latitude && longitude) {
      serviceUrl = SettingsService.api.searchPlacesByNameAndGeolocationProfileUrl;
    }
    if (!distance) {
      serviceUrl = SettingsService.api.searchPlacesByNameAndGeolocationProfileUrl;
    }

    return this.http.get(serviceUrl, { params }).pipe(
      map((response: HttpResponse<any>) => this.buildProfiles(response, 'places')),
      catchError((error) => throwError(error)),
    );
  }

  getPlacesByService(
    name: string,
    serviceCategory: string,
    service: string,
    specialty: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    pageNumber: number,
    pageSize: number,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): Observable<Profile[]> {
    const params = this.buildServiceSearchParams(
      name,
      serviceCategory,
      service,
      specialty,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      pageNumber,
      pageSize,
      latitude,
      longitude,
      distance,
      distanceKms,
    );

    let serviceUrl = SettingsService.api.searchPlacesByServiceProfileUrl;

    if (location) {
      serviceUrl = SettingsService.api.searchPlacesByServiceAndLocationProfileUrl;
    } else if (latitude && longitude) {
      serviceUrl = SettingsService.api.searchPlacesByServiceAndGeolocationProfileUrl;
    }
    if (!distance) {
      serviceUrl = SettingsService.api.searchPlacesByServiceAndGeolocationProfileUrl;
    }

    return this.http.get(serviceUrl, { params }).pipe(
      map((response: HttpResponse<any>) => this.buildProfiles(response, 'places')),
      catchError((error) => throwError(error)),
    );
  }

  getPeopleAndPlacesByService(
    name: string,
    serviceCategory: string,
    service: string,
    specialty: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    pageNumber: number,
    pageSize: number,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): Observable<Profile[][]> {
    // people
    const peopleParams = this.buildServiceSearchParams(
      name,
      serviceCategory,
      service,
      specialty,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      pageNumber,
      pageSize,
      latitude,
      longitude,
      distance,
      distanceKms,
    );

    let peopleUrl = SettingsService.api.searchPeopleByServiceProfileUrl;

    if (location) {
      peopleUrl = SettingsService.api.searchPeopleByServiceAndLocationProfileUrl;
    } else if (latitude && longitude) {
      peopleUrl = SettingsService.api.searchPeopleByServiceAndGeolocationProfileUrl;
    }
    if (!distance) {
      peopleUrl = SettingsService.api.searchPeopleByServiceAndGeolocationProfileUrl;
    }

    // places
    const placesParams = this.buildServiceSearchParams(
      name,
      serviceCategory,
      service,
      specialty,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      pageNumber,
      pageSize,
      latitude,
      longitude,
      distance,
      distanceKms,
    );

    let placesUrl = SettingsService.api.searchPlacesByServiceProfileUrl;

    if (location) {
      placesUrl = SettingsService.api.searchPlacesByServiceAndLocationProfileUrl;
    } else if (latitude && longitude) {
      placesUrl = SettingsService.api.searchPlacesByServiceAndGeolocationProfileUrl;
    }
    if (!distance) {
      placesUrl = SettingsService.api.searchPlacesByServiceAndGeolocationProfileUrl;
    }

    const peopleRequest = this.http.get(peopleUrl, { params: peopleParams }).pipe(
      map((response: HttpResponse<any>) => this.buildProfiles(response, 'people')),
      catchError((error) => throwError(error)),
    );

    const placeRequest = this.http.get(placesUrl, { params: placesParams }).pipe(
      map((response: HttpResponse<any>) => this.buildProfiles(response, 'places')),
      catchError((error) => throwError(error)),
    );

    return forkJoin([peopleRequest, placeRequest]);
  }

  getPeopleAndPlaces(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    pageNumber: number,
    pageSize: number,
    serviceCategory: string,
    service: string,
    specialty: string,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): Observable<Profile[][]> {
    // people
    const peopleParams = this.buildSearchParams(
      name,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      pageNumber,
      pageSize,
      serviceCategory,
      service,
      specialty,
      latitude,
      longitude,
      distance,
      distanceKms,
    );

    let peopleUrl = SettingsService.api.searchPeopleByNameProfileUrl;

    if (location) {
      peopleUrl = SettingsService.api.searchPeopleByNameAndLocationProfileUrl;
    } else if (latitude && longitude) {
      peopleUrl = SettingsService.api.searchPeopleByNameAndGeolocationProfileUrl;
    }
    if (!distance) {
      peopleUrl = SettingsService.api.searchPeopleByNameAndGeolocationProfileUrl;
    }

    // places
    const placesParams = this.buildSearchParams(
      name,
      location,
      affiliatedProvider,
      affiliatedProviderConsult,
      sort,
      easyClaim,
      telehealth,
      pageNumber,
      pageSize,
      serviceCategory,
      service,
      specialty,
      latitude,
      longitude,
      distance,
      distanceKms,
    );

    // let placesUrl = SettingsService.api.searchPlacesProfileUrl;
    let placesUrl = SettingsService.api.searchPlacesByNameProfileUrl;

    if (location) {
      placesUrl = SettingsService.api.searchPlacesByNameAndLocationProfileUrl;
    } else if (latitude && longitude) {
      placesUrl = SettingsService.api.searchPlacesByNameAndGeolocationProfileUrl;
    }
    if (!distance) {
      placesUrl = SettingsService.api.searchPlacesByNameAndGeolocationProfileUrl;
    }

    const peopleRequest = this.http.get(peopleUrl, { params: peopleParams }).pipe(
      map((response: HttpResponse<any>) => this.buildProfiles(response, 'people')),
      catchError((error) => throwError(error)),
    );

    const placeRequest = this.http.get(placesUrl, { params: placesParams }).pipe(
      map((response: HttpResponse<any>) => this.buildProfiles(response, 'places')),
      catchError((error) => throwError(error)),
    );

    return forkJoin([peopleRequest, placeRequest]);
  }

  buildSearchParams(
    name: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    pageNumber: number,
    pageSize: number,
    serviceCategory: string,
    service: string,
    specialty: string,
    latitude: string,
    // longitude: string): URLSearchParams { // old deprecated module upgraded to httpParams
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): HttpParams {
    // const params: URLSearchParams = new URLSearchParams(); // old deprecated module upgraded to httpParams
    let params: HttpParams = new HttpParams();
    params = !name ? params : params.append('name', name);
    params = !location ? params : params.append('location', location);
    params = !serviceCategory ? params : params.append('serviceCategory', this.stripWildCards(serviceCategory));
    params = !service ? params : params.append('service', this.stripWildCards(service));
    params = !specialty ? params : params.append('specialty', this.stripWildCards(specialty));
    params = !distance ? params : params.append('distanceKms', distanceKms.toString());

    if (affiliatedProvider === true) {
      params = params.append('isAffiliatedProvider', affiliatedProvider.toString());
    }
    params = params.append('sort', sort);
    params = params.append('isAffiliatedProviderConsult', affiliatedProviderConsult.toString());

    if (telehealth === true) {
      params = params.append('isTelehealth', telehealth.toString());
    }

    if (easyClaim === true) {
      params = params.append('isEasyClaim', easyClaim.toString());
    }

    params = !pageSize ? params : params.append('pageSize', pageSize.toString());
    params = !pageNumber ? params : params.append('pageNumber', pageNumber.toString());
    params = !latitude ? params : params.append('latitude', latitude.toString());
    params = !longitude ? params : params.append('longitude', longitude.toString());

    return params;
  }

  buildServiceSearchParams(
    name: string,
    serviceCategory: string,
    service: string,
    specialty: string,
    location: string,
    affiliatedProvider: boolean,
    affiliatedProviderConsult: boolean,
    sort: string,
    easyClaim: boolean,
    telehealth: boolean,
    pageNumber: number,
    pageSize: number,
    latitude: string,
    longitude: string,
    distance: boolean,
    distanceKms: number,
  ): HttpParams {
    let params: HttpParams = new HttpParams();

    params = !name ? params : params.append('name', name);
    params = !service ? params : params.append('service', service);
    params = !specialty ? params : params.append('specialty', specialty);
    params = !serviceCategory ? params : params.append('serviceCategory', serviceCategory);
    params = !location ? params : params.append('location', location);

    if (affiliatedProvider === true) {
      params = params.append('isAffiliatedProvider', affiliatedProvider.toString());
    }
    params = params.append('sort', sort);
    params = params.append('isAffiliatedProviderConsult', affiliatedProviderConsult.toString());
    if (telehealth === true) {
      params = params.append('isTelehealth', telehealth.toString());
    }
    if (easyClaim === true) {
      params = params.append('isEasyClaim', easyClaim.toString());
    }

    params = !pageSize ? params : params.append('pageSize', pageSize.toString());
    params = !pageNumber ? params : params.append('pageNumber', pageNumber.toString());
    params = !latitude ? params : params.append('latitude', latitude.toString());
    params = !longitude ? params : params.append('longitude', longitude.toString());
    params = !distance ? params : params.append('distanceKms', distanceKms.toString());

    return params;
  }

  buildProfiles(response, type: string): Profile[] {
    const results = response as WebApiModel.ProfileDetail[]; // revisit after angular 7 upgrade

    const profiles = results.map((data) => {
      const profile = new Profile();
      profile.data = data;
      profile.type = type;
      return profile;
    });

    return profiles;
  }

  stripWildCards(term: string): string {
    if (term) {
      return term.split('%').join('');
    }
    return term;
  }
}
